import { useCallback, useState } from "react";

type State = {
  key: string;
  /** Conteúdo do formulário a que a chave pertence. */
  fingerprint: string;
  /** Último envio com resposta perdida (pode ter sido gravado no servidor). */
  failed: { key: string; fingerprint: string } | null;
};

const newKey = () => crypto.randomUUID();

/**
 * `Idempotency-Key` de um formulário de criação (lançamento, transferência), conforme a decisão P1:
 * - a chave é a mesma enquanto o conteúdo (`values`) não muda: "Salvar" de novo depois de uma resposta perdida reenvia
 *   a mesma chave, e a API devolve o que já criou em vez de duplicar;
 * - muda quando qualquer campo muda (o pedido passa a ser outro) e no sucesso (`rotate`);
 * - `changedAfterFailure`: houve resposta perdida (`markFailure`) e depois o conteúdo mudou. A tela avisa antes de
 *   enviar, porque o salvamento anterior pode ter chegado. Voltar ao conteúdo da falha reusa a chave dela.
 */
export function useIdempotencyKey(values: unknown) {
  const fingerprint = JSON.stringify(values);
  const [state, setState] = useState<State>(() => ({ key: newKey(), fingerprint, failed: null }));

  let current = state;
  if (state.fingerprint !== fingerprint) {
    // Estado derivado do render anterior: o React descarta este render e refaz com o estado novo.
    current = {
      ...state,
      fingerprint,
      key: state.failed?.fingerprint === fingerprint ? state.failed.key : newKey(),
    };
    setState(current);
  }

  /**
   * Registra a resposta perdida. Passe o `snapshot()` tirado no INÍCIO do envio: se o usuário editou um campo
   * enquanto o pedido estava pendente, a falha tem de ficar com a chave e o conteúdo do pedido que saiu — senão o
   * próximo "Salvar" sairia com outra chave e sem o aviso, e poderia duplicar o que já foi gravado.
   */
  const markFailure = useCallback(
    (sent?: { key: string; fingerprint: string }) =>
      setState((s) => ({ ...s, failed: sent ?? { key: s.key, fingerprint: s.fingerprint } })),
    [],
  );
  const rotate = useCallback(() => {
    const key = newKey();
    setState((s) => ({ ...s, key, failed: null }));
    return key;
  }, []);

  return {
    key: current.key,
    fingerprint,
    /** Chave e conteúdo deste instante (para `markFailure` do envio que está começando). */
    snapshot: () => ({ key: current.key, fingerprint }),
    changedAfterFailure: current.failed !== null && current.failed.fingerprint !== fingerprint,
    markFailure,
    rotate,
  };
}
